/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { gql } from "graphql-tag";
import { useQuery, useMutation } from "@apollo/client";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Grid, Typography, Link as MuiLink } from "@mui/material";
import Modal from "components/modal/message-modal";
import { RemoveModal } from "components/modal/remove";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import {
  updateTemplateGroup,
  updateTemplateGroupVariables,
  removeTemplateGroup,
  removeTemplateGroupVariables,
  TemplateGroupCreateInput,
  DuplicateTemplateGroupVariables,
  DuplicateTemplateGroup,
} from "types/gql-types";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { Viewer } from "components/auth/types";
import { calculateMessageValues } from "utils/template";
import { TEMPLATE_DASHBOARD_ROUTE } from "../template-groups";
import { TemplateForm } from "./form";
import { DUPLICATE_TEMPLATE } from "./template-table";
import { REMOVE_TEMPLATE } from "./remove";

const SERVER_ERROR =
  "Unable to update template. Please try again or contact support@havenconnect.com.";

const DELETE_TEMPLATE_CONFIRM = "Are you sure you want to delete this template?";
const DELETE_ERROR =
  "Unable to delete this template. Please try again or contact support@havenconnect.com.";

const UPDATE_TEMPLATE_GROUP = gql`
  mutation updateTemplateGroup($id: ID!, $data: TemplateGroupCreateInput!) {
    updateTemplateGroup(id: $id, data: $data) {
      id
    }
  }
`;

const TEMPLATE = gql`
  query getTemplateGroup($id: ID!) {
    templateGroup(id: $id) {
      id
      name
      isOrgTemplate
      isPropertyTemplate
      properties {
        id
        interestListId
        name
      }
      canModify
      templates {
        id
        type
        html
        body
        subject
        isEmpty
      }
    }
  }
`;

const gqlUserToOption = (x: { id: string; name: string; interestListId: string }) => ({
  id: x.interestListId,
  label: x.name,
});

const UpdateTemplateGroups: React.FC<{viewer: Viewer}> =
(props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  if (!id) return <ServerError desc="Missing ID" />;
  const { loading, data, error } = useQuery(TEMPLATE, {
    variables: { id },
    fetchPolicy: "network-only",
  });

  const [updateTemplateGroupFunc, updateTemplateGroupMutation] = useMutation<
  updateTemplateGroup,
  updateTemplateGroupVariables
  >(UPDATE_TEMPLATE_GROUP, { refetchQueries: ["myTemplateGroups"] });
  const [duplicate] = useMutation<
  DuplicateTemplateGroup,
  DuplicateTemplateGroupVariables
  >(DUPLICATE_TEMPLATE);
  const [removeTemplate] = useMutation<
  removeTemplateGroup,
  removeTemplateGroupVariables
  >(REMOVE_TEMPLATE);
  const [serverError, setServerError] = useState(false);
  const { openSnackbar } = useSnackbar();

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;
  if (!data.templateGroup) return <ServerError desc="Missing Data" />;

  const closeModal = () => {
    setServerError(false);
  };

  const renderErrorModal = () => (
    <Modal
      isOpen={serverError}
      onSubmit={closeModal}
      onDismiss={closeModal}
      message={SERVER_ERROR}
    />
  );

  const template = data.templateGroup;
  const { canModify } = data.templateGroup;

  const handleDuplicate = () =>
    duplicate({ variables: { id }, refetchQueries: ["myTemplateGroups"] })
      .then(({ data: dupData }) => {
        const newId = dupData?.duplicateTemplateGroup?.id;
        if (newId) {
          navigate(`/dash/manager/settings/templates/update/${newId}`);
        } else {
          navigate(TEMPLATE_DASHBOARD_ROUTE);
        }
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({ message: "Error copying template", status: "error" });
      });

  const deleteAction = () =>
    removeTemplate({
      variables: { id },
      refetchQueries: ["templateGroups", "myTemplateGroups"],
    }).then(() => navigate(TEMPLATE_DASHBOARD_ROUTE));

  const onSubmit = (templateData: TemplateGroupCreateInput) => {
    if (!canModify) {
      duplicate({ variables: { id } })
        .then(() => navigate(TEMPLATE_DASHBOARD_ROUTE))
        .catch((err) => {
          logger.sendError(err);
          openSnackbar({ message: "Error copying template", status: "error" });
        });
      return;
    }
    updateTemplateGroupFunc({ variables: { data: templateData, id } })
      .then(() => {
        openSnackbar({ message: "Template updated successfully", status: "success", duration: 3000 });
      })
      .catch((err) => {
        logger.sendError(err);
        setServerError(true);
      });
  };

  if (data && data.templateGroup) {
    const templateData = data.templateGroup;
    const initialFormValues = {
      template: { name: template.name || "" },
      orgTemplate: template.isOrgTemplate,
      propertyTemplate: template.isPropertyTemplate,
      propertyIds: (template.properties || []).map(gqlUserToOption),
    };

    const messageProps = calculateMessageValues(templateData);
    return (
      <Grid container spacing={3}>
        {renderErrorModal()}
        <Grid size={{ xs: 12 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid>
                  <Typography variant="h6" component="div">
                    <MuiLink
                      component={Link}
                      data-cy="template-nav"
                      to={TEMPLATE_DASHBOARD_ROUTE}
                      color="primary"
                    >
                      Templates
                    </MuiLink>{" "}
                    &gt; {canModify ? "Update Template" : "View Template"}
                  </Typography>
                </Grid>
                <Grid>
                  <MuiLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleDuplicate();
                    }}
                    color="primary"
                  >
                    Copy
                  </MuiLink>
                  {canModify && (
                    <>
                      {" | "}
                      <RemoveModal
                        action={deleteAction}
                        buttonText="Delete"
                        removalText={DELETE_TEMPLATE_CONFIRM}
                        errorText={DELETE_ERROR}
                      />
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TemplateForm
                initialValues={initialFormValues}
                isSending={updateTemplateGroupMutation.loading}
                handleSubmit={onSubmit}
                viewer={props.viewer}
                onCancel={() => navigate(TEMPLATE_DASHBOARD_ROUTE)}
                readOnly={!templateData.canModify}
                {...messageProps}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  if (loading) return <LoadingSpinner />;
  return <div>Error fetching template</div>;
};

export default UpdateTemplateGroups;
